/**
 * The one badge catalog. Profile.tsx and GamificationHub.tsx used to each
 * keep their own separate, slightly different list -- overlapping on most
 * entries but not all (Profile had "Quest Initiate"/"Quest Master" milestones
 * GamificationHub didn't; GamificationHub had five per-quest badges Profile
 * didn't; both had a different, differently-named badge for "complete all 5
 * quests"). A learner visiting both pages could see two different sets of
 * "earned" badges for the same account. Merged into one list so that can't
 * happen again -- see the redesign commit for the full reconciliation.
 *
 * `earned` is derived from real stats (challenges completed, streak, points,
 * level, quests) rather than trusted from the server's own badge list,
 * because the backend's calculateBadges() (backend/src/index.js) only ever
 * awards six coarse badges with different names than any of these -- the
 * richer, tiered catalog here has always been computed client-side. That's
 * fine (the thresholds are real), but it means there is no real per-badge
 * earned date anywhere in this system: don't show one that looks real.
 */

export type BadgeRarity = 'common' | 'rare' | 'epic' | 'legendary';

/**
 * Rarity tiers use the design system's own escalating accents rather than
 * invented colors: neutral (nothing special yet) -> volt (the system's
 * existing secondary accent, see Landing's "Applied practice" section) ->
 * ember (the primary brand color) -> gold (already reserved for XP, the
 * app's one "precious" signifier). Same four tokens everywhere else in the
 * app already means something; reusing them here means "legendary" reads as
 * special without a new palette existing only on one page -- or, as of this
 * export, existing differently on two pages that used to each style rarity
 * their own way.
 */
export const RARITY_STYLE: Record<BadgeRarity, { border: string; bg: string; fg: string }> = {
  common: { border: 'var(--border-strong)', bg: 'var(--surface-sunken)', fg: 'var(--text-secondary)' },
  rare: { border: 'var(--volt)', bg: 'var(--volt-soft)', fg: 'var(--volt-strong)' },
  epic: { border: 'var(--ember)', bg: 'var(--ember-soft)', fg: 'var(--ember-strong)' },
  legendary: { border: 'var(--gold)', bg: 'oklch(0.8 0.15 85 / 0.16)', fg: 'oklch(0.55 0.13 75)' },
};

export interface BadgeDefinition {
  id: string;
  name: string;
  description: string;
  icon: string;
  rarity: BadgeRarity;
}

export interface BadgeStats {
  challengesCompleted: number;
  streak: number;
  points: number;
  level: number;
  questsCompleted: number;
}

export const BADGE_CATALOG: BadgeDefinition[] = [
  { id: 'newcomer', name: 'New Recruit', description: 'Welcome aboard!', icon: 'Star', rarity: 'common' },
  { id: 'first-success', name: 'First Mission', description: 'Completed first challenge', icon: 'Target', rarity: 'common' },
  { id: 'challenge-5', name: 'Quick Learner', description: 'Complete 5 challenges', icon: 'Zap', rarity: 'common' },
  { id: 'challenge-15', name: 'Practice Ace', description: 'Complete 15 challenges', icon: 'Award', rarity: 'rare' },
  { id: 'challenge-30', name: 'Elite Commander', description: 'Complete 30 challenges', icon: 'Shield', rarity: 'epic' },
  { id: 'challenge-50', name: 'Grandmaster', description: 'Complete 50 challenges', icon: 'Crown', rarity: 'legendary' },
  { id: 'streak-3', name: 'Momentum Builder', description: 'Maintain a 3-day streak', icon: 'Zap', rarity: 'common' },
  { id: 'streak-7', name: 'Consistent Streak', description: 'Maintain a 7-day streak', icon: 'Rocket', rarity: 'rare' },
  { id: 'streak-14', name: 'Unstoppable Streak', description: 'Maintain a 14-day streak', icon: 'Shield', rarity: 'epic' },
  { id: 'streak-30', name: 'Iron Discipline', description: 'Maintain a 30-day streak', icon: 'Star', rarity: 'legendary' },
  { id: 'points-500', name: 'Focus Stone', description: 'Earn 500 credits', icon: 'Gem', rarity: 'common' },
  { id: 'points-1000', name: 'Archive Keeper', description: 'Earn 1,000 credits', icon: 'ScrollText', rarity: 'rare' },
  { id: 'points-2500', name: 'Archive Master', description: 'Earn 2,500 credits', icon: 'Library', rarity: 'epic' },
  { id: 'points-5000', name: 'Grand Champion', description: 'Earn 5,000 credits', icon: 'Sparkles', rarity: 'legendary' },
  { id: 'level-3', name: 'Apprentice Graduate', description: 'Reach Level 3', icon: 'GraduationCap', rarity: 'rare' },
  { id: 'level-5', name: 'Grand Master', description: 'Reach Level 5', icon: 'Trophy', rarity: 'legendary' },
  { id: 'first-quest', name: 'Quest Initiate', description: 'Complete your first quest', icon: 'Map', rarity: 'rare' },
  { id: 'quest-master', name: 'Quest Master', description: 'Complete 3 quests', icon: 'Award', rarity: 'epic' },
  { id: 'quest-spy', name: 'Master Spy', description: 'Complete the Galactic Spy quest', icon: 'Eye', rarity: 'rare' },
  { id: 'quest-escape', name: 'Ice Survivor', description: 'Complete Ice Planet Escape', icon: 'Snowflake', rarity: 'rare' },
  { id: 'quest-archive', name: 'Artifact Hunter', description: 'Complete the Archive Hunt', icon: 'Map', rarity: 'rare' },
  { id: 'quest-sensei', name: 'Dojo Adept', description: "Complete the Sensei's Trials", icon: 'Sparkle', rarity: 'epic' },
  { id: 'quest-time', name: 'Chronomaster', description: 'Complete the Time Paradox', icon: 'Clock', rarity: 'epic' },
  { id: 'quest-all', name: 'Quest Legend', description: 'Complete all 5 quests', icon: 'Star', rarity: 'legendary' },
];

/** Which quest ids `quest-spy`/`quest-escape`/etc. actually check, so this stays in one place. */
const QUEST_BADGE_IDS: { [badgeId: string]: string } = {
  'quest-spy': 'galactic-spy',
  'quest-escape': 'ice-planet-escape',
  'quest-archive': 'archive-hunt',
  'quest-sensei': 'sensei-trials',
  'quest-time': 'time-paradox',
};

export interface EarnedBadge extends BadgeDefinition {
  earned: boolean;
}

/**
 * Every badge, with `earned` computed from real stats -- `earnedBadgeNames`
 * (whatever the server's own badge list says) only adds names this catalog
 * doesn't otherwise recognize a threshold for; it never overrides a
 * threshold that's already false, since a stale or partial server list
 * should never make an unearned badge look earned.
 */
export function computeBadges(stats: BadgeStats, completedQuests: string[] = [], earnedBadgeNames: string[] = []): EarnedBadge[] {
  return BADGE_CATALOG.map((def) => {
    let earned: boolean;
    switch (def.id) {
      case 'newcomer': earned = true; break;
      case 'first-success': earned = stats.challengesCompleted >= 1; break;
      case 'challenge-5': earned = stats.challengesCompleted >= 5; break;
      case 'challenge-15': earned = stats.challengesCompleted >= 15; break;
      case 'challenge-30': earned = stats.challengesCompleted >= 30; break;
      case 'challenge-50': earned = stats.challengesCompleted >= 50; break;
      case 'streak-3': earned = stats.streak >= 3; break;
      case 'streak-7': earned = stats.streak >= 7; break;
      case 'streak-14': earned = stats.streak >= 14; break;
      case 'streak-30': earned = stats.streak >= 30; break;
      case 'points-500': earned = stats.points >= 500; break;
      case 'points-1000': earned = stats.points >= 1000; break;
      case 'points-2500': earned = stats.points >= 2500; break;
      case 'points-5000': earned = stats.points >= 5000; break;
      case 'level-3': earned = stats.level >= 3; break;
      case 'level-5': earned = stats.level >= 5; break;
      case 'first-quest': earned = stats.questsCompleted >= 1; break;
      case 'quest-master': earned = stats.questsCompleted >= 3; break;
      case 'quest-all': earned = stats.questsCompleted >= 5; break;
      default:
        earned = QUEST_BADGE_IDS[def.id] ? completedQuests.includes(QUEST_BADGE_IDS[def.id]) : false;
    }
    return { ...def, earned: earned || earnedBadgeNames.includes(def.name) };
  });
}
